let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Linking,
  Alert,
  Modal,
  ActivityIndicator,
  Dimensions,
  TextInput,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Ionicons,
  FontAwesome5,
  MaterialIcons,
  MaterialCommunityIcons,
  AntDesign,
  Feather,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width } = Dimensions.get('window');

// Default company logo placeholder
const defaultLogo = {
  uri: 'https://via.placeholder.com/100/0e4e96/FFFFFF?text=Company',
};

// ** INTEGRATION: Enhanced Interview Service **
const InterviewService = {
  async saveInterviewUpdate(interviewId, updates) {
    try {
      console.log('💾 Saving interview update:', { interviewId, updates });
      const key = `interview_${interviewId}`;
      const existingData = await AsyncStorage.getItem(key);
      const interviewData = existingData ? JSON.parse(existingData) : {};
      
      const updatedData = {
        ...interviewData,
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await AsyncStorage.setItem(key, JSON.stringify(updatedData));
      
      // Emit update event for other screens
      this.emitInterviewUpdate(interviewId, updatedData);
      
      return updatedData;
    } catch (error) {
      console.error('❌ Error saving interview update:', error);
      throw error;
    }
  },

  async getInterviewData(interviewId) {
    try {
      const key = `interview_${interviewId}`;
      const data = await AsyncStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('❌ Error getting interview data:', error);
      return null;
    }
  },

  emitInterviewUpdate(interviewId, data) {
    // In a real app, this would use a proper event system
    console.log('📡 Interview update emitted:', { interviewId, data });
  },

  async cancelInterview(interviewId, reason) {
    try {
      console.log('❌ Cancelling interview:', { interviewId, reason });
      
      const updates = {
        status: 'cancelled',
        outcome: 'cancelled',
        cancellationReason: reason,
        cancelledAt: new Date().toISOString()
      };
      
      return await this.saveInterviewUpdate(interviewId, updates);
    } catch (error) {
      console.error('❌ Error cancelling interview:', error);
      throw error;
    }
  },

  async updatePreparationStatus(interviewId, preparationType, completed) {
    try {
      console.log('📚 Updating preparation status:', { interviewId, preparationType, completed });
      
      const existingData = await this.getInterviewData(interviewId) || {};
      const preparationCompleted = existingData.preparationCompleted || {};
      
      const updates = {
        preparationCompleted: {
          ...preparationCompleted,
          [preparationType]: completed
        }
      };
      
      return await this.saveInterviewUpdate(interviewId, updates);
    } catch (error) {
      console.error('❌ Error updating preparation status:', error);
      throw error;
    }
  }
};

export default function InterviewDetailsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_interviewdetailsscreen`;
    apiFetch('/api/data/interviews?sort=interview_date&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();
  const route = useRoute();
  
  // ** INTEGRATION: Enhanced parameter extraction **
  const { 
    interview, 
    interviewId, 
    id, 
    source = '',
    highlightInterview = false 
  } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showInterviewerModal, setShowInterviewerModal] = useState(false);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // ** INTEGRATION: Enhanced sample interview details **
  const sampleInterviewDetails = {
    id: interview?.id || interviewId || id || '1',
    company: interview?.company || 'Atkins Global',
    position: interview?.position || 'Project Manager',
    type: interview?.type || 'Video Interview',
    date: interview?.date || 'May 20, 2025',
    time: interview?.time || '10:00 AM',
    duration: interview?.duration || '45 min',
    status: interview?.status || 'scheduled',
    outcome: interview?.outcome || 'pending',
    logo: interview?.logo || defaultLogo,
    location: interview?.location || 'Remote (Video Call)',
    description: interview?.description ||
      'This interview will focus on your project management experience, leadership skills, and technical knowledge. Please be prepared to discuss your past projects and how you handled challenges.',
    aiMatchScore: interview?.aiMatchScore || 88,
    preparationCompleted: interview?.preparationCompleted || {
      aiPractice: false,
      competencyTest: true,
      psychologyTest: false,
      arPortfolio: false,
    },
    interviewers: interview?.interviewers || [
      {
        name: 'Sarah Johnson',
        title: 'HR Manager',
        avatar: null,
        bio: 'Sarah has been with Atkins Global for 5 years and specializes in recruiting engineering talent. She will be assessing your cultural fit and soft skills.',
        linkedin: 'https://linkedin.com/in/sarahjohnson',
      },
      {
        name: 'Michael Chen',
        title: 'Senior Project Manager',
        avatar: null,
        bio: 'Michael leads the infrastructure projects division and has over 15 years of experience in the field. He will be evaluating your technical knowledge and project management approach.',
        linkedin: 'https://linkedin.com/in/michaelchen',
      },
    ],
    documents: interview?.documents || [
      {
        id: 'doc1',
        name: 'Interview Preparation Guide',
        type: 'PDF',
        size: '1.2 MB',
        url: 'https://example.com/docs/prep-guide.pdf',
      },
      {
        id: 'doc2',
        name: 'Company Overview',
        type: 'PDF',
        size: '3.5 MB',
        url: 'https://example.com/docs/company-overview.pdf',
      },
      {
        id: 'doc3',
        name: 'Project Case Study',
        type: 'DOCX',
        size: '2.1 MB',
        url: 'https://example.com/docs/case-study.docx',
      },
    ],
    agenda: interview?.agenda || [
      {
        time: '10:00 AM - 10:10 AM',
        activity: 'Introduction and Overview',
        description: 'Meet the interview panel and discuss the interview format.',
      },
      {
        time: '10:10 AM - 10:30 AM',
        activity: 'Experience and Background Discussion',
        description: 'Review of your past projects and relevant experience.',
      },
      {
        time: '10:30 AM - 10:50 AM',
        activity: 'Technical Assessment',
        description: 'Questions about your technical skills and problem-solving approach.',
      },
      {
        time: '10:50 AM - 11:00 AM',
        activity: 'Your Questions',
        description: 'Opportunity for you to ask questions about the role and company.',
      },
    ],
    tips: interview?.tips || [
      'Research recent Atkins Global projects and company values',
      'Prepare examples of your project management experience',
      'Be ready to discuss how you handle project challenges',
      'Have questions prepared about the company culture and team structure',
      'Test your video and audio equipment before the interview',
    ],
    notes: interview?.notes || '',
    nextSteps: interview?.nextSteps || 'Prepare for the interview',
    salary: interview?.salary || 'Competitive salary package',
    benefits: interview?.benefits || ['Health Insurance', 'Pension', 'Flexible Working'],
  };

  useEffect(() => {
    loadInterviewDetails();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // DB loader — pulls REAL interview + company info.
  // Chain of joins:
  //   1. application_interviews/:id        → core interview row
  //   2. applications/:application_id      → candidate + job id
  //   3. jobs/:job_id                      → job title + company_id
  //   4. company-profile/:company_id       → name, logo, industry, etc.
  // If the route params already contain a full interview envelope (from
  // VideoInterviewScreen, InterviewHistory etc.), we use that as the base
  // and only fetch what's missing.
  // ─────────────────────────────────────────────────────────────────────────
  const loadInterviewDetails = async () => {
    try {
      setLoading(true);

      const realId = interview?.id || interviewId || id;
      const numericId = (realId && /^\d+$/.test(String(realId))) ? String(realId) : null;

      // Start from whatever the caller passed in, OR an empty shell.
      let base = {
        ...(interview || {}),
        id: realId,
      };

      // 1. Fetch the interview row when we have a numeric id.
      let ivRow = null;
      if (numericId) {
        try {
          const r = await apiFetch(`/api/data/application_interviews/${numericId}`);
          ivRow = Array.isArray(r) ? r[0]
                : (r?.data && !Array.isArray(r.data)) ? r.data
                : (Array.isArray(r?.data) ? r.data[0] : r);
          if (ivRow?.id) {
            base = {
              ...base,
              id:            String(ivRow.id),
              applicationId: ivRow.application_id,
              type:          (ivRow.interview_type || 'video') + ' interview',
              status:        ivRow.status || 'scheduled',
              scheduledAt:   ivRow.scheduled_date,
              date:          ivRow.scheduled_date ? new Date(ivRow.scheduled_date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : (base.date || ''),
              time:          ivRow.scheduled_date ? new Date(ivRow.scheduled_date).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })          : (base.time || ''),
              duration:      ivRow.duration_minutes ? `${ivRow.duration_minutes} minutes` : (base.duration || '60 minutes'),
              meetingLink:   ivRow.meeting_link || base.meetingLink || '',
              meetingId:     ivRow.meeting_id || base.meetingId || '',
              meetingPassword: ivRow.meeting_password || base.meetingPassword || '',
              location:      ivRow.location || base.location || '',
              instructions:  ivRow.instructions || base.instructions || '',
              notes:         ivRow.notes || base.notes || '',
              roundNumber:   ivRow.round_number || base.roundNumber || 1,
              recordingEnabled: ivRow.recording_enabled !== false,
              recordingUrl:  ivRow.recording_url || '',
              interviewers:  Array.isArray(ivRow.interviewers) ? ivRow.interviewers : (base.interviewers || []),
            };
            console.log('[InterviewDetails] loaded interview row id=', ivRow.id);
          }
        } catch (e) { console.warn('[InterviewDetails] interview fetch:', e?.message); }
      }

      // 2. Resolve the application row → application_id maps to candidate + job_id
      let appRow = null;
      const appId = ivRow?.application_id || base.applicationId;
      if (appId && /^\d+$/.test(String(appId))) {
        try {
          const r = await apiFetch(`/api/data/applications/${appId}`);
          appRow = Array.isArray(r) ? r[0]
                 : (r?.data && !Array.isArray(r.data)) ? r.data
                 : (Array.isArray(r?.data) ? r.data[0] : r);
          if (appRow?.id) {
            base.position = appRow.job_title || base.position || '';
            base.candidateName = appRow.candidate_name || base.candidateName || '';
            base.candidateEmail = appRow.candidate_email || '';
            console.log('[InterviewDetails] loaded application id=', appRow.id);
          }
        } catch (e) { console.warn('[InterviewDetails] application fetch:', e?.message); }
      }

      // 3. Resolve the job row → job_id → company_id + title
      let jobRow = null;
      const jobId = appRow?.job_id || base.jobId;
      if (jobId && /^\d+$/.test(String(jobId))) {
        try {
          const r = await apiFetch(`/api/data/jobs/${jobId}`);
          jobRow = Array.isArray(r) ? r[0]
                 : (r?.data && !Array.isArray(r.data)) ? r.data
                 : (Array.isArray(r?.data) ? r.data[0] : r);
          if (jobRow?.id) {
            base.position = jobRow.title || base.position || '';
            base.jobId    = jobRow.id;
            base.jobLocation = jobRow.location || base.jobLocation || '';
            console.log('[InterviewDetails] loaded job id=', jobRow.id);
          }
        } catch (e) { console.warn('[InterviewDetails] job fetch:', e?.message); }
      }

      // 4. Company — pull the FULL profile so the UI can render rich details.
      // Try canonical /api/company-profile/:id (singular, JOINed view) first;
      // fall back to /api/data/company_profile then /api/data/company_profiles.
      const companyId = jobRow?.company_id || base.companyId;
      if (companyId) {
        let companyRow = null;
        try {
          const r = await apiFetch(`/api/company-profile/${companyId}`);
          companyRow = (r?.data && !Array.isArray(r.data)) ? r.data
                     : (Array.isArray(r?.data) ? r.data[0] : r);
          if (!companyRow || (!companyRow.name && !companyRow.company_name)) companyRow = null;
        } catch {}
        if (!companyRow) {
          try {
            const r = await apiFetch(`/api/data/company_profile?id=${companyId}&limit=1`);
            companyRow = Array.isArray(r) ? r[0]
                       : (Array.isArray(r?.data) ? r.data[0] : r);
          } catch {}
        }
        if (!companyRow) {
          try {
            const r = await apiFetch(`/api/data/company_profiles?id=${companyId}&limit=1`);
            companyRow = Array.isArray(r) ? r[0]
                       : (Array.isArray(r?.data) ? r.data[0] : r);
          } catch {}
        }
        if (companyRow) {
          base.company         = companyRow.name || companyRow.company_name || base.company || '';
          base.companyLogo     = companyRow.logo || null;
          base.companyIndustry = companyRow.industry || '';
          base.companyLocation = companyRow.headquarters || companyRow.city || '';
          base.companyWebsite  = companyRow.website || '';
          base.companySize     = companyRow.employee_count || null;
          base.companyAbout    = companyRow.description || '';
          base.companyId       = companyRow.id;
          base.companyDetails  = companyRow;
          // Cache for other screens
          await AsyncStorage.setItem('company_profile', JSON.stringify(companyRow)).catch(() => {});
          console.log('[InterviewDetails] loaded company id=', companyRow.id, 'name=', base.company);
        }
      }

      // If we got nothing at all, leave the screen showing whatever route params
      // provided rather than dumping in a fake "Atkins Global" placeholder.
      // The UI safely renders blank fields, and a soft alert (not blocking)
      // tells the user we couldn't load any details.
      if (!ivRow && !interview) {
        console.warn('[InterviewDetails] no DB row, no route param — showing empty shell');
      }

      setInterviewDetails(base);
      setLoading(false);

      if (source)             console.log(`🔗 Navigated from ${source} to InterviewDetails`);
      if (highlightInterview) console.log('✨ Interview highlighted for user attention');
    } catch (error) {
      console.error('[InterviewDetails] load error:', error);
      // Final fallback — at least show route param data instead of crashing.
      setInterviewDetails(interview || {});
      setLoading(false);
    }
  };

  // Function to get color based on AI Match Score
  const getMatchScoreColor = score => {
    if (score >= 90) return '#10B981'; // Green
    if (score >= 80) return '#fdb913'; // Yellow
    if (score >= 70) return '#F59E0B'; // Orange
    return '#EF4444'; // Red
  };

  // ** INTEGRATION: Enhanced join interview **
  const handleJoinInterview = () => {
    Alert.alert(
      'Join Interview',
      'Are you ready to join the video interview?',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Join Now',
          onPress: async () => {
            try {
              // Update interview status
              await InterviewService.saveInterviewUpdate(interviewDetails.id, {
                status: 'in_progress',
                joinedAt: new Date().toISOString()
              });
              
              // Navigate to video interview
              navigation.navigate('VideoInterview', {
                interviewData: interviewDetails,
                source: 'InterviewDetails'
              });
            } catch (error) {
              console.error('❌ Error joining interview:', error);
              Alert.alert('Error', 'Failed to join interview. Please try again.');
            }
          },
        },
      ]
    );
  };

  // ** INTEGRATION: Enhanced reschedule navigation **
  const handleReschedule = () => {
    console.log('🔄 Navigating to RescheduleInterview');
    navigation.navigate('RescheduleInterview', { 
      interview: interviewDetails,
      source: 'InterviewDetails'
    });
  };

  // ** INTEGRATION: Enhanced cancel interview **
  const handleCancelInterview = () => {
    setShowCancelModal(true);
  };

  // ** INTEGRATION: Enhanced confirm cancellation **
  const handleConfirmCancellation = async () => {
    if (!cancellationReason.trim()) {
      Alert.alert('Missing Information', 'Please provide a reason for cancellation.');
      return;
    }

    try {
      setIsUpdating(true);

      const ivId = interviewDetails?.id;
      const numericId = (ivId && /^\d+$/.test(String(ivId))) ? String(ivId) : null;

      // 1. Persist to DB first — without this, the cancellation only lives
      //    in local storage and the recruiter's view never updates.
      let dbOk = false;
      if (numericId) {
        try {
          await apiFetch(`/api/data/application_interviews/${numericId}`, {
            method: 'PUT',
            body: JSON.stringify({
              status: 'cancelled',
              notes: (interviewDetails?.notes ? interviewDetails.notes + '\n\n' : '')
                   + `Cancelled by candidate: ${cancellationReason.trim()}`,
            }),
          });
          dbOk = true;
          console.log('[InterviewDetails] cancel PUT ok id=', numericId);
        } catch (e) {
          console.warn('[InterviewDetails] cancel PUT failed:', e?.message);
        }
      }

      // 2. Local service mirror (offline cache)
      try { await InterviewService.cancelInterview(ivId, cancellationReason); } catch {}

      setShowCancelModal(false);

      Alert.alert(
        'Interview Cancelled',
        dbOk
          ? 'Your interview has been cancelled. The recruiter will be notified.'
          : 'Saved locally. We will sync the cancellation when you reconnect.',
        [
          {
            text: 'OK',
            onPress: () => {
              if (source === 'InterviewHistory') {
                navigation.navigate('InterviewHistory', {
                  refreshData: true,
                  updatedInterviewId: ivId,
                });
              } else {
                navigation.navigate('TestsInterviews', {
                  refreshData: true,
                });
              }
            },
          },
        ]
      );
    } catch (error) {
      console.error('[InterviewDetails] cancel error:', error);
      Alert.alert('Error', 'Failed to cancel interview. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ** INTEGRATION: Enhanced document handling **
  const handleViewDocument = document => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handleDownloadDocument = document => {
    Alert.alert('Download Started', `Downloading ${document.name}...`);
    // In a real app, this would download the document
  };

  // Handle interviewer profile view
  const handleViewInterviewer = interviewer => {
    setSelectedInterviewer(interviewer);
    setShowInterviewerModal(true);
  };

  // Handle LinkedIn profile
  const handleOpenLinkedIn = url => {
    Linking.openURL(url);
  };

  // ** INTEGRATION: Enhanced preparation tracking **
  const handleStartPreparation = async (preparationType) => {
    try {
      setIsUpdating(true);
      
      // Update preparation status
      await InterviewService.updatePreparationStatus(interviewDetails.id, preparationType, true);
      
      // Update local state
      setInterviewDetails(prev => ({
        ...prev,
        preparationCompleted: {
          ...prev.preparationCompleted,
          [preparationType]: true
        }
      }));
      
      // Navigate to appropriate screen
      switch (preparationType) {
        case 'aiPractice':
          navigation.navigate('TestsInterviews', { 
            tab: 'interviews',
            source: 'InterviewDetails' 
          });
          break;
        case 'competencyTest':
          navigation.navigate('TestsInterviews', { 
            tab: 'assessments',
            source: 'InterviewDetails' 
          });
          break;
        case 'arPortfolio':
          navigation.navigate('PortfolioScreen', { 
            source: 'InterviewDetails' 
          });
          break;
        default:
          navigation.navigate('TestsInterviews');
      }
    } catch (error) {
      console.error('❌ Error updating preparation status:', error);
      Alert.alert('Error', 'Failed to update preparation status.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ** INTEGRATION: Navigation back to history **
  const navigateToInterviewHistory = () => {
    console.log('🔙 Navigating back to InterviewHistory');
    navigation.navigate('InterviewHistory', { 
      source: 'InterviewDetails',
      highlightInterviewId: interviewDetails.id 
    });
  };

  // Render loading state
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Interview Details</Text>
          <View style={{ width: 32 }}>{/* Empty view for balance */}</View>
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading interview details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Interview Details</Text>
        <View style={styles.headerRight}>
          {source === 'InterviewHistory' && (
            <TouchableOpacity
              style={styles.headerAction}
              onPress={navigateToInterviewHistory}
            >
              <Ionicons name="list-outline" size={24} color="#ffffff" />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.headerAction}
            onPress={() => {
              // Share interview details
              Alert.alert('Share', 'Interview details would be shared here');
            }}
          >
            <Ionicons name="share-outline" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
      >
        {/* Source indicator */}
        {source && (
          <View style={styles.sourceIndicator}>
            <Ionicons name="information-circle-outline" size={16} color="#007AFF" />
            <Text style={styles.sourceText}>From {source}</Text>
          </View>
        )}

        {/* Interview Info Card */}
        <View style={[styles.interviewCard, highlightInterview && styles.highlightedCard]}>
          <View style={styles.interviewHeader}>
            <Image source={interviewDetails.logo} style={styles.companyLogo} />
            <View style={styles.interviewHeaderText}>
              <Text style={styles.companyName}>{interviewDetails.company}</Text>
              <Text style={styles.positionName}>
                {interviewDetails.position}
              </Text>
            </View>
            {/* AI Match Score Display */}
            <View style={styles.aiMatchContainer}>
              <Text
                style={[
                  styles.aiMatchScore,
                  { color: getMatchScoreColor(interviewDetails.aiMatchScore) },
                ]}
              >
                {interviewDetails.aiMatchScore}%
              </Text>
              <Text style={styles.aiMatchLabel}>AI Match</Text>
            </View>
          </View>

          <View style={styles.interviewDetails}>
            <View style={styles.detailItem}>
              <FontAwesome5 name="calendar-alt" size={16} color="#64748B" />
              <Text style={styles.detailText}>{interviewDetails.date}</Text>
            </View>
            <View style={styles.detailItem}>
              <Ionicons name="time-outline" size={16} color="#64748B" />
              <Text style={styles.detailText}>
                {interviewDetails.time} ({interviewDetails.duration})
              </Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialIcons name="video-call" size={16} color="#64748B" />
              <Text style={styles.detailText}>{interviewDetails.type}</Text>
            </View>
            <View style={styles.detailItem}>
              <Ionicons name="location-outline" size={16} color="#64748B" />
              <Text style={styles.detailText}>{interviewDetails.location}</Text>
            </View>
          </View>

          <View style={styles.statusContainer}>
            <View style={[
              styles.statusBadge,
              interviewDetails.status === 'cancelled' && styles.cancelledBadge,
              interviewDetails.status === 'completed' && styles.completedBadge
            ]}>
              <Text style={[
                styles.statusText,
                interviewDetails.status === 'cancelled' && styles.cancelledText,
                interviewDetails.status === 'completed' && styles.completedText
              ]}>
                {interviewDetails.status === 'scheduled' ? 'Upcoming' : 
                 interviewDetails.status === 'cancelled' ? 'Cancelled' :
                 interviewDetails.status === 'completed' ? 'Completed' :
                 interviewDetails.status}
              </Text>
            </View>

            {/* Countdown Timer (if interview is upcoming) */}
            {interviewDetails.status === 'scheduled' && (
              <View style={styles.countdownContainer}>
                <Text style={styles.countdownLabel}>Interview in:</Text>
                <Text style={styles.countdownText}>4 days, 2 hours</Text>
              </View>
            )}
          </View>

          {/* Action Buttons */}
          {interviewDetails.status === 'scheduled' && (
            <View style={styles.actionButtonsContainer}>
              <TouchableOpacity
                style={[styles.actionButton, styles.primaryButton]}
                onPress={handleJoinInterview}
              >
                <MaterialIcons name="video-call" size={20} color="#ffffff" />
                <Text style={styles.primaryButtonText}>Join Interview</Text>
              </TouchableOpacity>

              <View style={styles.secondaryButtonsRow}>
                <TouchableOpacity
                  style={[styles.secondaryButton, styles.rescheduleButton]}
                  onPress={handleReschedule}
                >
                  <MaterialIcons name="schedule" size={16} color="#0e4e96" />
                  <Text style={styles.secondaryButtonText}>Reschedule</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.secondaryButton, styles.cancelButton]}
                  onPress={handleCancelInterview}
                >
                  <MaterialIcons name="close" size={16} color="#EF4444" />
                  <Text
                    style={[styles.secondaryButtonText, { color: '#EF4444' }]}
                  >
                    Cancel
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Cancelled interview info */}
          {interviewDetails.status === 'cancelled' && (
            <View style={styles.cancelledInfo}>
              <Text style={styles.cancelledTitle}>Interview Cancelled</Text>
              {interviewDetails.cancellationReason && (
                <Text style={styles.cancelledReason}>
                  Reason: {interviewDetails.cancellationReason}
                </Text>
              )}
              {interviewDetails.cancelledAt && (
                <Text style={styles.cancelledDate}>
                  Cancelled on: {new Date(interviewDetails.cancelledAt).toLocaleDateString()}
                </Text>
              )}
            </View>
          )}
        </View>

        {/* Interview Description */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>About This Interview</Text>
          <Text style={styles.descriptionText}>
            {interviewDetails.description}
          </Text>
        </View>

        {/* Preparation Status */}
        {interviewDetails.status === 'scheduled' && (
          <View style={styles.sectionContainer}>
            <Text style={styles.sectionTitle}>Preparation Status</Text>

            <View style={styles.preparationContainer}>
              <View style={styles.preparationItem}>
                <View
                  style={[
                    styles.preparationIcon,
                    interviewDetails.preparationCompleted.aiPractice
                      ? styles.completedIcon
                      : styles.pendingIcon,
                  ]}
                >
                  {interviewDetails.preparationCompleted.aiPractice ? (
                    <Ionicons name="checkmark" size={16} color="#ffffff" />
                  ) : (
                    <MaterialCommunityIcons
                      name="robot"
                      size={16}
                      color="#ffffff"
                    />
                  )}
                </View>
                <View style={styles.preparationTextContainer}>
                  <Text style={styles.preparationTitle}>
                    AI Interview Practice
                  </Text>
                  <Text style={styles.preparationStatus}>
                    {interviewDetails.preparationCompleted.aiPractice
                      ? 'Completed'
                      : 'Not Started'}
                  </Text>
                </View>
                {!interviewDetails.preparationCompleted.aiPractice && (
                  <TouchableOpacity
                    style={styles.startButton}
                    onPress={() => handleStartPreparation('aiPractice')}
                    disabled={isUpdating}
                  >
                    <Text style={styles.startButtonText}>Start</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.preparationItem}>
                <View
                  style={[
                    styles.preparationIcon,
                    interviewDetails.preparationCompleted.competencyTest
                      ? styles.completedIcon
                      : styles.pendingIcon,
                  ]}
                >
                  {interviewDetails.preparationCompleted.competencyTest ? (
                    <Ionicons name="checkmark" size={16} color="#ffffff" />
                  ) : (
                    <MaterialCommunityIcons
                      name="clipboard-check"
                      size={16}
                      color="#ffffff"
                    />
                  )}
                </View>
                <View style={styles.preparationTextContainer}>
                  <Text style={styles.preparationTitle}>Competency Test</Text>
                  <Text style={styles.preparationStatus}>
                    {interviewDetails.preparationCompleted.competencyTest
                      ? 'Completed'
                      : 'Not Started'}
                  </Text>
                </View>
                {!interviewDetails.preparationCompleted.competencyTest && (
                  <TouchableOpacity
                    style={styles.startButton}
                    onPress={() => handleStartPreparation('competencyTest')}
                    disabled={isUpdating}
                  >
                    <Text style={styles.startButtonText}>Start</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.preparationItem}>
                <View
                  style={[
                    styles.preparationIcon,
                    interviewDetails.preparationCompleted.arPortfolio
                      ? styles.completedIcon
                      : styles.pendingIcon,
                  ]}
                >
                  {interviewDetails.preparationCompleted.arPortfolio ? (
                    <Ionicons name="checkmark" size={16} color="#ffffff" />
                  ) : (
                    <MaterialCommunityIcons
                      name="view-dashboard"
                      size={16}
                      color="#ffffff"
                    />
                  )}
                </View>
                <View style={styles.preparationTextContainer}>
                  <Text style={styles.preparationTitle}>AR Portfolio</Text>
                  <Text style={styles.preparationStatus}>
                    {interviewDetails.preparationCompleted.arPortfolio
                      ? 'Completed'
                      : 'Not Started'}
                  </Text>
                </View>
                {!interviewDetails.preparationCompleted.arPortfolio && (
                  <TouchableOpacity
                    style={styles.startButton}
                    onPress={() => handleStartPreparation('arPortfolio')}
                    disabled={isUpdating}
                  >
                    <Text style={styles.startButtonText}>Start</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Interview Agenda */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Interview Agenda</Text>
          {interviewDetails.agenda.map((item, index) => (
            <View key={index} style={styles.agendaItem}>
              <View style={styles.agendaTimeContainer}>
                <Text style={styles.agendaTime}>{item.time}</Text>
              </View>
              <View style={styles.agendaContent}>
                <Text style={styles.agendaActivity}>{item.activity}</Text>
                <Text style={styles.agendaDescription}>{item.description}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Interviewers */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Your Interviewers</Text>
          {interviewDetails.interviewers.map((interviewer, index) => (
            <TouchableOpacity
              key={index}
              style={styles.interviewerItem}
              onPress={() => handleViewInterviewer(interviewer)}
            >
              <View style={styles.interviewerAvatar}>
                {interviewer.avatar ? (
                  <Image source={interviewer.avatar} style={styles.avatarImage} />
                ) : (
                  <Text style={styles.avatarText}>
                    {interviewer.name
                      .split(' ')
                      .map(n => n[0])
                      .join('')}
                  </Text>
                )}
              </View>
              <View style={styles.interviewerInfo}>
                <Text style={styles.interviewerName}>{interviewer.name}</Text>
                <Text style={styles.interviewerTitle}>{interviewer.title}</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color="#64748B" />
            </TouchableOpacity>
          ))}
        </View>

        {/* Documents */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Interview Documents</Text>
          {interviewDetails.documents.map((document, index) => (
            <TouchableOpacity
              key={index}
              style={styles.documentItem}
              onPress={() => handleViewDocument(document)}
            >
              <View style={styles.documentIcon}>
                <MaterialIcons
                  name={document.type === 'PDF' ? 'picture-as-pdf' : 'description'}
                  size={24}
                  color="#EF4444"
                />
              </View>
              <View style={styles.documentInfo}>
                <Text style={styles.documentName}>{document.name}</Text>
                <Text style={styles.documentMeta}>
                  {document.type} • {document.size}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.downloadButton}
                onPress={() => handleDownloadDocument(document)}
              >
                <Ionicons name="download-outline" size={20} color="#0e4e96" />
              </TouchableOpacity>
            </TouchableOpacity>
          ))}
        </View>

        {/* Interview Tips */}
        <View style={styles.sectionContainer}>
          <Text style={styles.sectionTitle}>Interview Tips</Text>
          {interviewDetails.tips.map((tip, index) => (
            <View key={index} style={styles.tipItem}>
              <View style={styles.tipBullet}>
                <Text style={styles.tipBulletText}>•</Text>
              </View>
              <Text style={styles.tipText}>{tip}</Text>
            </View>
          ))}
        </View>

        {/* Additional Information */}
        {(interviewDetails.notes || interviewDetails.nextSteps || interviewDetails.salary) && (
          <View style={styles.sectionContainer}>
            <Text style={styles.sectionTitle}>Additional Information</Text>
            
            {interviewDetails.notes && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Notes:</Text>
                <Text style={styles.infoText}>{interviewDetails.notes}</Text>
              </View>
            )}
            
            {interviewDetails.nextSteps && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Next Steps:</Text>
                <Text style={styles.infoText}>{interviewDetails.nextSteps}</Text>
              </View>
            )}
            
            {interviewDetails.salary && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Salary:</Text>
                <Text style={styles.infoText}>{interviewDetails.salary}</Text>
              </View>
            )}
            
            {interviewDetails.benefits && interviewDetails.benefits.length > 0 && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Benefits:</Text>
                <Text style={styles.infoText}>{interviewDetails.benefits.join(', ')}</Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Cancel Interview Modal */}
      <Modal
        visible={showCancelModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowCancelModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Cancel Interview</Text>
            <Text style={styles.modalMessage}>
              Are you sure you want to cancel your interview with {interviewDetails?.company}?
            </Text>
            
            <View style={styles.reasonContainer}>
              <Text style={styles.reasonLabel}>Reason for cancellation:</Text>
              <TextInput
                style={styles.reasonInput}
                placeholder="Please provide a reason..."
                placeholderTextColor="#94A3B8"
                multiline
                numberOfLines={3}
                value={cancellationReason}
                onChangeText={setCancellationReason}
              />
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setShowCancelModal(false)}
              >
                <Text style={styles.modalCancelText}>Keep Interview</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalConfirmButton}
                onPress={handleConfirmCancellation}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <ActivityIndicator size={20} color="#ffffff" />
                ) : (
                  <Text style={styles.modalConfirmText}>Cancel Interview</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Document Modal */}
      <Modal
        visible={showDocumentModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowDocumentModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.documentModalContent}>
            <View style={styles.documentModalHeader}>
              <Text style={styles.documentModalTitle}>
                {selectedDocument?.name}
              </Text>
              <TouchableOpacity onPress={() => setShowDocumentModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.documentPreview}>
              <MaterialIcons
                name={selectedDocument?.type === 'PDF' ? 'picture-as-pdf' : 'description'}
                size={80}
                color="#EF4444"
              />
              <Text style={styles.documentPreviewText}>
                Document preview would appear here
              </Text>
            </View>

            <View style={styles.documentModalButtons}>
              <TouchableOpacity
                style={styles.documentButton}
                onPress={() => handleDownloadDocument(selectedDocument)}
              >
                <Ionicons name="download-outline" size={20} color="#0e4e96" />
                <Text style={styles.documentButtonText}>Download</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.documentButton}
                onPress={() => {
                  // Open document in external app
                  Alert.alert('Open Document', 'Document would open in external app');
                }}
              >
                <Ionicons name="open-outline" size={20} color="#0e4e96" />
                <Text style={styles.documentButtonText}>Open</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Interviewer Modal */}
      <Modal
        visible={showInterviewerModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowInterviewerModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.interviewerModalContent}>
            <View style={styles.interviewerModalHeader}>
              <Text style={styles.interviewerModalTitle}>Interviewer Profile</Text>
              <TouchableOpacity onPress={() => setShowInterviewerModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            {selectedInterviewer && (
              <View style={styles.interviewerProfile}>
                <View style={styles.interviewerProfileAvatar}>
                  {selectedInterviewer.avatar ? (
                    <Image source={selectedInterviewer.avatar} style={styles.profileAvatarImage} />
                  ) : (
                    <Text style={styles.profileAvatarText}>
                      {selectedInterviewer.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')}
                    </Text>
                  )}
                </View>
                
                <Text style={styles.interviewerProfileName}>
                  {selectedInterviewer.name}
                </Text>
                <Text style={styles.interviewerProfileTitle}>
                  {selectedInterviewer.title}
                </Text>
                
                {selectedInterviewer.bio && (
                  <Text style={styles.interviewerProfileBio}>
                    {selectedInterviewer.bio}
                  </Text>
                )}

                {selectedInterviewer.linkedin && (
                  <TouchableOpacity
                    style={styles.linkedinButton}
                    onPress={() => handleOpenLinkedIn(selectedInterviewer.linkedin)}
                  >
                    <FontAwesome5 name="linkedin" size={20} color="#0077B5" />
                    <Text style={styles.linkedinButtonText}>View LinkedIn Profile</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  headerRight: {
    flexDirection: 'row',
    gap: 8,
  },
  headerAction: {
    padding: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    padding: 16,
  },
  sourceIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 16,
    gap: 8,
  },
  sourceText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  interviewCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  highlightedCard: {
    borderWidth: 2,
    borderColor: '#007AFF',
    shadowColor: '#007AFF',
    shadowOpacity: 0.2,
  },
  interviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  companyLogo: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  interviewHeaderText: {
    flex: 1,
  },
  companyName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  positionName: {
    fontSize: 16,
    color: '#64748B',
  },
  aiMatchContainer: {
    alignItems: 'center',
  },
  aiMatchScore: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  aiMatchLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  interviewDetails: {
    marginBottom: 16,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  detailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 12,
  },
  statusContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  statusBadge: {
    backgroundColor: '#10B981',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  cancelledBadge: {
    backgroundColor: '#EF4444',
  },
  completedBadge: {
    backgroundColor: '#10B981',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#ffffff',
  },
  cancelledText: {
    color: '#ffffff',
  },
  completedText: {
    color: '#ffffff',
  },
  countdownContainer: {
    alignItems: 'flex-end',
  },
  countdownLabel: {
    fontSize: 12,
    color: '#64748B',
  },
  countdownText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
  },
  actionButtonsContainer: {
    gap: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  primaryButton: {
    backgroundColor: '#0e4e96',
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  secondaryButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 6,
  },
  rescheduleButton: {
    borderColor: '#0e4e96',
    backgroundColor: '#ffffff',
  },
  cancelButton: {
    borderColor: '#EF4444',
    backgroundColor: '#ffffff',
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0e4e96',
  },
  cancelledInfo: {
    backgroundColor: '#FEF2F2',
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
  },
  cancelledTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#EF4444',
    marginBottom: 8,
  },
  cancelledReason: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  cancelledDate: {
    fontSize: 12,
    color: '#94A3B8',
  },
  sectionContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 16,
  },
  descriptionText: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  preparationContainer: {
    gap: 16,
  },
  preparationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  preparationIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  completedIcon: {
    backgroundColor: '#10B981',
  },
  pendingIcon: {
    backgroundColor: '#94A3B8',
  },
  preparationTextContainer: {
    flex: 1,
  },
  preparationTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 2,
  },
  preparationStatus: {
    fontSize: 14,
    color: '#64748B',
  },
  startButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  startButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  agendaItem: {
    flexDirection: 'row',
    marginBottom: 16,
    gap: 12,
  },
  agendaTimeContainer: {
    minWidth: 100,
  },
  agendaTime: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0e4e96',
  },
  agendaContent: {
    flex: 1,
  },
  agendaActivity: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 4,
  },
  agendaDescription: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: 18,
  },
  interviewerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    gap: 12,
  },
  interviewerAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  interviewerInfo: {
    flex: 1,
  },
  interviewerName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 2,
  },
  interviewerTitle: {
    fontSize: 14,
    color: '#64748B',
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    gap: 12,
  },
  documentIcon: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  documentInfo: {
    flex: 1,
  },
  documentName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 2,
  },
  documentMeta: {
    fontSize: 12,
    color: '#64748B',
  },
  downloadButton: {
    padding: 8,
  },
  tipItem: {
    flexDirection: 'row',
    marginBottom: 8,
    gap: 8,
  },
  tipBullet: {
    width: 20,
    alignItems: 'center',
  },
  tipBulletText: {
    fontSize: 16,
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  tipText: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  infoItem: {
    marginBottom: 12,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  infoText: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  reasonContainer: {
    marginBottom: 20,
  },
  reasonLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 8,
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#1E293B',
    textAlignVertical: 'top',
    minHeight: 80,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  modalConfirmButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#EF4444',
    alignItems: 'center',
  },
  modalConfirmText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  documentModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  documentModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  documentModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
    flex: 1,
    marginRight: 16,
  },
  documentPreview: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    marginBottom: 20,
  },
  documentPreviewText: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 16,
    textAlign: 'center',
  },
  documentModalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  documentButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#0e4e96',
    gap: 8,
  },
  documentButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0e4e96',
  },
  interviewerModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  interviewerModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  interviewerModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
  },
  interviewerProfile: {
    alignItems: 'center',
  },
  interviewerProfileAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  profileAvatarImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  profileAvatarText: {
    fontSize: 24,
    fontWeight: '600',
    color: '#ffffff',
  },
  interviewerProfileName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
    textAlign: 'center',
  },
  interviewerProfileTitle: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 16,
    textAlign: 'center',
  },
  interviewerProfileBio: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 20,
  },
  linkedinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#F0F8FF',
    gap: 8,
  },
  linkedinButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0077B5',
  },
});

